import { CardShell } from '@atlas/ui-components'
import { Button, Checkbox, Chip, IconButton } from '@atlas/ui-primitives'
import { Trash2 } from 'lucide-react'
import { isChangeRequestResolved } from '@/domain/change-requests'
import type { ChangeRequest } from '@/domain/workflow.types'
import { Restricted } from '@/lib/refusal'

const CREATED_AT_FORMAT: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' }

/** Which of an item's two ticks the checkboxes show and set: the worker's `done`, or the reviewer's `verified`. */
export type ChangeRequestTick = 'done' | 'verified'

function statusChip(changeRequest: ChangeRequest, tick: ChangeRequestTick): { label: string; ok: boolean } {
	if (isChangeRequestResolved(changeRequest)) return { label: 'Resolved', ok: true }
	if (tick === 'verified') return { label: 'Awaiting verification', ok: false }
	return changeRequest.items.every((item) => item.checked)
		? { label: 'Done', ok: true }
		: { label: 'Resolve before sending back', ok: false }
}

/**
 * One authored change request: who raised it and when, its checkable items,
 * and whether it is still outstanding — rendered identically wherever a
 * change request appears, so the validation workspace that raises one and the
 * working workspace that acts on it cannot drift apart.
 *
 * The two removal affordances live here and ONLY here, which is what keeps
 * them from overlapping:
 * - `onRemoveItem` renders a per-item control, but only while the request
 *   holds more than one item. Removing the last item is the request-level
 *   action, not an item action — otherwise two differently-labelled controls
 *   perform the same gesture at one item and the user cannot tell which they
 *   just used.
 * - `onDelete` renders the single control that removes the whole request.
 *
 * Omitting a callback omits its control, which is how the read-mostly view
 * gets toggle-only access without a second implementation.
 *
 * `tick` keeps the two sides apart: the working workspace ticks items done,
 * the validation workspace verifies them, and neither sees the other's ticks.
 *
 * `lockedReason` refuses every control through `Restricted`, for a reader who
 * may see the request but not act on it. `tickLockedReason` refuses only the
 * ticks, for a request whose ticks are frozen while it can still be edited.
 *
 * A resolved request STAYS rendered, marked resolved. Resolving is not
 * deleting: erasing the record of a resolved change request is precisely what
 * this system of record must not do.
 */
export function ChangeRequestCard({
	changeRequest,
	tick,
	onToggleItem,
	onRemoveItem,
	onDelete,
	lockedReason,
	tickLockedReason,
}: {
	changeRequest: ChangeRequest
	tick: ChangeRequestTick
	/** Omit to render the ticks read-only. */
	onToggleItem?: (itemId: string, checked: boolean) => void
	/** Omit to render no per-item removal control. */
	onRemoveItem?: (itemId: string) => void
	/** Omit to render no whole-request removal control. */
	onDelete?: () => void
	/** Set to refuse every control, explaining why on hover, focus and press. */
	lockedReason?: string
	/** Set to refuse the ticks alone, the same way. */
	tickLockedReason?: string
}) {
	const chip = statusChip(changeRequest, tick)
	const canRemoveItems = !!onRemoveItem && changeRequest.items.length > 1

	return (
		<CardShell as="li" className={chip.ok ? 'p-4' : 'border-ds-danger-strong p-4'}>
			<div className="mb-3 flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
				<p className="text-ds-sm text-ds-text-muted">
					Raised by <span className="font-medium text-ds-text">{changeRequest.authorName}</span>
					{' · '}
					{new Date(changeRequest.createdAt).toLocaleString(undefined, CREATED_AT_FORMAT)}
				</p>
				<Chip variant={chip.ok ? 'success' : 'danger'} size="sm">
					{chip.label}
				</Chip>
			</div>
			{/* One wrapper for every control: the card is refused as a whole, so it
			    has one hover target and one explanation. */}
			<Restricted allowed={!lockedReason} why={lockedReason ?? ''} className="w-full flex-col items-start">
				<ul className="w-full space-y-2">
					{changeRequest.items.map((item) => (
						<li key={item.itemId} className="flex items-center justify-between gap-2">
							<Restricted allowed={!tickLockedReason} why={tickLockedReason ?? ''}>
								<Checkbox
									size="sm"
									isSelected={tick === 'verified' ? item.verified : item.checked}
									isReadOnly={!onToggleItem}
									onChange={(checked) => onToggleItem?.(item.itemId, checked)}
								>
									{item.label}
								</Checkbox>
							</Restricted>
							{canRemoveItems ? (
								<IconButton
									size="sm"
									variant="muted"
									aria-label={`Remove item: ${item.label}`}
									onPress={() => onRemoveItem?.(item.itemId)}
								>
									<Trash2 className="h-3.5 w-3.5" />
								</IconButton>
							) : null}
						</li>
					))}
				</ul>
				{onDelete ? (
					<Button variant="danger" size="sm" className="mt-3" onPress={onDelete}>
						Delete change request
					</Button>
				) : null}
			</Restricted>
		</CardShell>
	)
}
